import {
  Button,
  Group,
  NativeSelect,
  NumberInput,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import type { z } from "zod";

import {
  getGetStaffTeamQueryKey,
  getListStaffTeamsQueryKey,
  useAdjustStaffCredits,
  useCancelStaffJob,
  useGrantStaffCredits,
} from "../../api/generated/endpoints/staff/staff";
import {
  StaffAdjustment,
  StaffJobCancel,
  StaffLotGrant,
  StaffPlanPeriodGrant,
} from "../../api/generated/zod";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { useIdempotencyKey } from "../../platform/idempotency";
import { isUuid } from "../../platform/routes";

/** Reads the team's detail and the list again after a staff write. */
function useRefreshTeam(teamId: string) {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: getGetStaffTeamQueryKey(teamId) }),
      queryClient.invalidateQueries({ queryKey: getListStaffTeamsQueryKey() }),
    ]);
}

/** What to fix, shown on the input it names, so the input is marked invalid and described. */
type Problem = { field: string; text: string } | undefined;
const errorOn = (problem: Problem, field: string) =>
  problem?.field === field ? problem.text : undefined;
const REASON = "Say why, in up to 500 characters.";
const NOT_ZERO = "Enter whole credits other than 0: negative takes credits away.";

const asInt = (value: number | string): number =>
  typeof value === "number" ? value : Number.NaN;
const failedField = (error: z.ZodError) => String(error.issues[0]?.path[0] ?? "");

/** The first day of next month (UTC), the latest month a plan period may be granted for (A7). */
function nextMonth(now: Date): string {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1))
    .toISOString()
    .slice(0, 10);
}

/**
 * Credits for a team (A7). The demo's normal case is this month of the internal plan's credits;
 * next month, or a one-off lot with a reason, are the others.
 */
export function GrantForm({ teamId }: { teamId: string }) {
  const refresh = useRefreshTeam(teamId);
  const idempotency = useIdempotencyKey();
  const [kind, setKind] = useState<"plan_period" | "lot">("plan_period");
  const [planCode, setPlanCode] = useState("internal");
  const [month, setMonth] = useState<"this" | "next">("this");
  const [credits, setCredits] = useState<number | string>(1000);
  const [reason, setReason] = useState("");
  const [problem, setProblem] = useState<Problem>(undefined);
  const grant = useGrantStaffCredits({ mutation: { onSuccess: refresh } });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsed =
      kind === "plan_period"
        ? StaffPlanPeriodGrant.safeParse({
            kind,
            plan_code: planCode.trim(),
            reason: reason.trim(),
            // This month is the API's default when none is named.
            ...(month === "next" ? { period_start: nextMonth(new Date()) } : {}),
          })
        : StaffLotGrant.safeParse({ kind, credits: asInt(credits), reason: reason.trim() });
    if (!parsed.success) {
      const field = failedField(parsed.error);
      setProblem(
        field === "reason"
          ? { field, text: REASON }
          : field === "plan_code"
            ? { field, text: "Enter a plan code, such as internal." }
            : { field: "credits", text: "Enter whole credits, from 1 to 10,000,000." },
      );
      return;
    }
    setProblem(undefined);
    const key = idempotency.keyFor(parsed.data);
    grant.mutate(
      { teamId, data: parsed.data, headers: { "Idempotency-Key": key } },
      { onError: (error) => idempotency.failed(key, parsed.data, error) },
    );
  };
  return (
    <form onSubmit={submit} noValidate>
      <Stack gap="xs" maw={480}>
        <Title order={3} size="h5">
          Grant credits
        </Title>
        <SegmentedControl
          aria-label="Grant"
          data={[
            { value: "plan_period", label: "A month of a plan" },
            { value: "lot", label: "One-off credits" },
          ]}
          value={kind}
          onChange={(value) => setKind(value === "lot" ? "lot" : "plan_period")}
        />
        {kind === "plan_period" ? (
          <Group grow align="flex-start">
            <TextInput
              label="Plan"
              value={planCode}
              error={errorOn(problem, "plan_code")}
              onChange={(event) => setPlanCode(event.currentTarget.value)}
            />
            <NativeSelect
              label="Month"
              data={[
                { value: "this", label: "This month" },
                { value: "next", label: "Next month" },
              ]}
              value={month}
              onChange={(event) => setMonth(event.currentTarget.value === "next" ? "next" : "this")}
            />
          </Group>
        ) : (
          <NumberInput
            label="Credits"
            min={1}
            allowDecimal={false}
            value={credits}
            error={errorOn(problem, "credits")}
            onChange={setCredits}
          />
        )}
        <TextInput
          label="Reason"
          value={reason}
          error={errorOn(problem, "reason")}
          onChange={(event) => setReason(event.currentTarget.value)}
        />
        {grant.isError ? <ErrorAlert error={grant.error} /> : null}
        {grant.isSuccess ? (
          <Text component="output" size="sm">
            Granted {grant.data.credits} credits.
          </Text>
        ) : null}
        <Group>
          <Button type="submit" loading={grant.isPending}>
            Grant credits
          </Button>
        </Group>
      </Stack>
    </form>
  );
}

/** A signed correction with its reason; a negative one past the balance is refused. */
export function AdjustForm({ teamId }: { teamId: string }) {
  const refresh = useRefreshTeam(teamId);
  const idempotency = useIdempotencyKey();
  const [credits, setCredits] = useState<number | string>("");
  const [reason, setReason] = useState("");
  const [problem, setProblem] = useState<Problem>(undefined);
  const adjust = useAdjustStaffCredits({ mutation: { onSuccess: refresh } });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const amount = asInt(credits);
    const parsed = StaffAdjustment.safeParse({ credits: amount, reason: reason.trim() });
    if (!parsed.success || amount === 0) {
      setProblem(
        parsed.success || failedField(parsed.error) === "credits"
          ? { field: "credits", text: NOT_ZERO }
          : { field: "reason", text: REASON },
      );
      return;
    }
    setProblem(undefined);
    const key = idempotency.keyFor(parsed.data);
    adjust.mutate(
      { teamId, data: parsed.data, headers: { "Idempotency-Key": key } },
      { onError: (error) => idempotency.failed(key, parsed.data, error) },
    );
  };
  return (
    <form onSubmit={submit} noValidate>
      <Stack gap="xs" maw={480}>
        <Title order={3} size="h5">
          Adjust credits
        </Title>
        <NumberInput
          label="Change in credits"
          allowDecimal={false}
          value={credits}
          error={errorOn(problem, "credits")}
          onChange={setCredits}
        />
        <TextInput
          label="Reason for the change"
          value={reason}
          error={errorOn(problem, "reason")}
          onChange={(event) => setReason(event.currentTarget.value)}
        />
        {adjust.isError ? <ErrorAlert error={adjust.error} /> : null}
        {adjust.isSuccess ? (
          <Text component="output" size="sm">
            Done. Available now: {adjust.data.available_after} credits.
          </Text>
        ) : null}
        <Group>
          <Button type="submit" variant="light" loading={adjust.isPending}>
            Adjust credits
          </Button>
        </Group>
      </Stack>
    </form>
  );
}

/** Cancel a stuck job of the team; its hold is released (A7, D10: staff only). */
export function CancelJobForm(props: { teamId: string; initialJobId?: string }) {
  const { teamId, initialJobId = "" } = props;
  const refresh = useRefreshTeam(teamId);
  const idempotency = useIdempotencyKey();
  const [jobId, setJobId] = useState(initialJobId);
  const [reason, setReason] = useState("");
  const [problem, setProblem] = useState<Problem>(undefined);
  const cancel = useCancelStaffJob({ mutation: { onSuccess: refresh } });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const id = jobId.trim().toLowerCase();
    const parsed = StaffJobCancel.safeParse({ reason: reason.trim() });
    if (!isUuid(id)) {
      setProblem({ field: "job_id", text: "Enter the job's id, from its page." });
      return;
    }
    if (!parsed.success) {
      setProblem({ field: "reason", text: REASON });
      return;
    }
    setProblem(undefined);
    const body = { jobId: id, ...parsed.data };
    const key = idempotency.keyFor(body);
    cancel.mutate(
      { teamId, jobId: id, data: parsed.data, headers: { "Idempotency-Key": key } },
      { onError: (error) => idempotency.failed(key, body, error) },
    );
  };
  return (
    <form onSubmit={submit} noValidate>
      <Stack gap="xs" maw={480}>
        <Title order={3} size="h5">
          Cancel a job
        </Title>
        <TextInput
          label="Job id"
          value={jobId}
          error={errorOn(problem, "job_id")}
          onChange={(event) => setJobId(event.currentTarget.value)}
        />
        <TextInput
          label="Reason for canceling"
          value={reason}
          error={errorOn(problem, "reason")}
          onChange={(event) => setReason(event.currentTarget.value)}
        />
        {cancel.isError ? <ErrorAlert error={cancel.error} /> : null}
        {cancel.isSuccess ? (
          <Text component="output" size="sm">
            Canceled; it was {cancel.data.previous_status}.
          </Text>
        ) : null}
        <Group>
          <Button type="submit" variant="light" color="red" loading={cancel.isPending}>
            Cancel the job
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
